import React from "react";

type PaginationItem = number | "ellipsis";

type AdminTablePaginationProps = {
  currentPage: number;
  totalPages: number;
  disabled?: boolean;
  onPageChange: (page: number) => void;
  nextDataCy?: string;
};

export const getAdminPaginationItems = (currentPage: number, totalPages: number): PaginationItem[] => {
  const safeTotalPages = Math.max(1, totalPages);
  const safeCurrentPage = Math.min(Math.max(1, currentPage), safeTotalPages);

  if (safeTotalPages <= 5) {
    return Array.from({ length: safeTotalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, safeTotalPages, safeCurrentPage]);

  if (safeCurrentPage === 1) {
    pages.add(2);
  } else if (safeCurrentPage === safeTotalPages) {
    pages.add(safeTotalPages - 1);
  } else {
    pages.add(safeCurrentPage - 1);
    pages.add(safeCurrentPage + 1);
  }

  return Array.from(pages)
    .sort((left, right) => left - right)
    .reduce<PaginationItem[]>((items, page) => {
      const previous = items[items.length - 1];
      if (typeof previous === "number" && page - previous > 1) {
        items.push("ellipsis");
      }
      items.push(page);
      return items;
    }, []);
};

const AdminTablePagination = ({
  currentPage,
  totalPages,
  disabled = false,
  onPageChange,
  nextDataCy,
}: AdminTablePaginationProps) => {
  const safeTotalPages = Math.max(1, totalPages);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < safeTotalPages;
  const paginationItems = getAdminPaginationItems(currentPage, safeTotalPages);

  return (
    <nav className="admin-table-pagination" aria-label="Pagination">
      <button
        type="button"
        className="admin-table-pagination__button"
        aria-label="Previous page"
        disabled={!canGoPrev || disabled}
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
      >
        ‹
      </button>

      {paginationItems.map((item, index) => (
        item === "ellipsis" ? (
          <span
            key={`ellipsis-${index}`}
            className="admin-table-pagination__ellipsis"
            aria-hidden="true"
          >
            ...
          </span>
        ) : (
          <button
            key={item}
            type="button"
            className={`admin-table-pagination__button${item === currentPage ? " is-active" : ""}`}
            aria-current={item === currentPage ? "page" : undefined}
            aria-label={`Page ${item}`}
            disabled={disabled || item === currentPage}
            onClick={() => onPageChange(item)}
          >
            {item}
          </button>
        )
      ))}

      <button
        type="button"
        className="admin-table-pagination__button"
        aria-label="Next page"
        disabled={!canGoNext || disabled}
        onClick={() => onPageChange(Math.min(safeTotalPages, currentPage + 1))}
        data-cy={nextDataCy}
      >
        ›
      </button>
    </nav>
  );
};

export default AdminTablePagination;
